import { useCallback, useMemo, useState } from 'react';

import { EventQueryOptions, usePaginatedEvents, UsePaginatedEventsParams } from '@restDataAccess';
import { Typography } from '@scaleaq/scaleui';
import { PaginationQuery, SiteControlEvent } from '@scaleaq/sitecontrol-lib';
import { ColumnFiltersState, PaginationState, SortingState, Updater } from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';

import { ColumnType, SiteControlColumn, Table } from '@/components/table/';
import { useLayoutProps } from '@/hooks';

import { useInventoryContext } from '../InventoryProvider/InventoryContext.ts';
import { EventTableConfig } from '../types.ts';
import { getEventTypeFilter, getSourceTypeFilter } from './historicalEvents/getEventTypeFilter.ts';

type HistoricalEventsProps = {
  columns: ColumnType<SiteControlEvent>[];
  configInput: EventTableConfig;
  defaultTableConfig: EventTableConfig;
  id: string;
  tableColumns?: SiteControlColumn<SiteControlEvent>[];
};

export const HistoricalEvents = ({
  columns,
  configInput,
  defaultTableConfig,
  id,
  tableColumns,
}: HistoricalEventsProps) => {
  const { t } = useTranslation(['eventStream']);
  const { getInventoryIdByName } = useInventoryContext();

  const { config, update: updateEventTableConfig } = useLayoutProps<EventTableConfig>({
    widgetId: id,
    configInput,
    defaultConfig: { ...defaultTableConfig, title: t('eventStream:eventStream') },
  });
  const {
    historicalPageSize,
    historyDefaultFilters: columnFilters,
    historyDefaultSorting: sorting = [{ id: 'timestamp', desc: true }],
    from: startTime,
    to: endTime,
  } = config;

  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: historicalPageSize || 20,
  });

  const handleSortingChange = useCallback(
    (sorting: Updater<SortingState>) => {
      const resolvedSorting =
        typeof sorting === 'function' ? sorting(config.historyDefaultSorting) : sorting;
      updateEventTableConfig({
        widgetId: id,
        config: {
          ...config,
          historyDefaultSorting: resolvedSorting,
        },
      });

      // When sorting changes, reset to the first page
      setPagination(prevState => ({
        ...prevState,
        pageIndex: 0,
      }));
    },
    [config, id, updateEventTableConfig]
  );

  const handleColumnFiltersChange = useCallback(
    (columnFilters: Updater<ColumnFiltersState>) => {
      const resolvedFilters =
        typeof columnFilters === 'function'
          ? columnFilters(config.historyDefaultFilters)
          : columnFilters;

      updateEventTableConfig({
        widgetId: id,
        config: {
          ...config,
          historyDefaultFilters: resolvedFilters,
        },
      });

      // When sorting changes, reset to the first page
      setPagination(prevState => ({
        ...prevState,
        pageIndex: 0,
      }));
    },
    [config, id, updateEventTableConfig]
  );

  const paginatedQueryOptions: UsePaginatedEventsParams = useMemo<UsePaginatedEventsParams>(() => {
    const sortDirection: 'Ascending' | 'Descending' =
      `${sorting?.[0]?.desc ? 'Descending' : 'Ascending'}`;
    const eventQueryOptions: EventQueryOptions = {
      endTime,
      eventType: getEventTypeFilter(columnFilters),
      inventoryId: getInventoryIdByName(getSourceTypeFilter(columnFilters)),
      startTime,
      sortDirection,
    };
    const paginationQuery: PaginationQuery = {
      page: pagination.pageIndex + 1, // OData uses 1-based indexing for pages
      pageSize: pagination.pageSize || historicalPageSize || 20,
      includeCount: true,
    };
    return { eventQueryOptions, paginationQuery };
  }, [
    columnFilters,
    endTime,
    getInventoryIdByName,
    historicalPageSize,
    pagination.pageIndex,
    pagination.pageSize,
    sorting,
    startTime,
  ]);

  const { data: events, isFetching: isEventsLoading } = usePaginatedEvents(paginatedQueryOptions);

  const data = useMemo(() => {
    return events ? events.items : [];
  }, [events]);

  const key = useMemo(() => {
    return events && events.items.length > 0
      ? events.items[0].timestamp.toString()
      : Date.now().toString();
  }, [events]);
  return (
    <>
      {isEventsLoading && <Typography>{t('eventsLoading')}</Typography>}
      {!isEventsLoading && events && (
        <Table
          enableSorting={true}
          columns={columns}
          tableColumns={tableColumns}
          data={data}
          rowCount={events.totalCount || 0}
          onPaginationChange={setPagination}
          onSortingChange={handleSortingChange}
          onColumnFiltersChange={handleColumnFiltersChange}
          state={{ pagination, sorting, columnFilters }}
          key={key}
        />
      )}
    </>
  );
};
